import { Button, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect, useState } from 'react'
import {
  InformationModal,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  ArtistUpdateOrganizationNotificationInput,
  Maybe,
  OfficialInformationConsoleBoxFragment,
} from '@/clients/utoniq-core/schema'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { useForm, UseFormProps } from '@/core/form'

type ConsoleOfficialInformationEditFormFormProps = {
  officialInformation: Maybe<OfficialInformationConsoleBoxFragment>
  onSubmit: (values: ConsoleOfficialInformationEditFormValues) => Promise<void>
}

export type ConsoleOfficialInformationEditFormValues =
  ArtistUpdateOrganizationNotificationInput

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(30)
    .label(tLabel('consoleOfficialInformationEdit.title')),
  body: o
    .string()
    .max(3000)
    .optional()
    .allow(null, '')
    .label(tLabel('consoleOfficialInformationEdit.body')),
  link: o
    .string()
    .optional()
    .allow(null, '')
    .label(tLabel('consoleOfficialInformationEdit.link')),
})

export const ConsoleOfficialInformationEditFormForm: Organism<ConsoleOfficialInformationEditFormFormProps> =
  ({ officialInformation, onSubmit }) => {
    const { t } = useTranslation('consoleOfficialInformationEdit')
    const { showError } = useMessage()
    const [openConfirm, setOpenConfirm] = useState<boolean>(false)

    const {
      formState: { errors, isValid, isSubmitting },
      reset,
      register,
      setError,
      handleSubmit,
    } = useForm<ConsoleOfficialInformationEditFormValues>({
      schema,
      defaultValues,
    })

    useEffect(() => {
      if (!officialInformation) return
      reset({
        title: officialInformation.title ?? '',
        body: officialInformation.body ?? '',
        link: officialInformation.link ?? '',
      })
    }, [officialInformation])

    const handleOnSubmit = handleSubmit(async values => {
      await onSubmit(values).catch(error => {
        handleError<ConsoleOfficialInformationEditFormValues>(
          error,
          setError,
          showError
        )
      })
    })

    return (
      <OrganismFrame sx={{ pt: 8 }}>
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 2,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          {...register('title')}
          error={errors?.title}
          displayRequire={{ display: true }}
        />
        <TextInputCell
          type="textarea"
          textFieldProps={{
            multiline: true,
            rows: 4,
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          {...register('body')}
          error={errors?.body}
        />
        <TextInputLinkCell
          type="text"
          {...register('link')}
          error={errors?.link}
        />
        <ButtonCell
          submitting={isSubmitting}
          disabled={isSubmitting || !isValid}
          onSubmit={handleSubmit(() => setOpenConfirm(true))}
        />
        <InformationModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          title={t('confirmEditTitle')}
          content={t('confirmEditDesc')}
          buttons={
            <Button
              size="large"
              sx={{ width: 212 }}
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={handleOnSubmit}
            >
              {t('change')}
            </Button>
          }
          cancelText={t('cancel')}
        />
      </OrganismFrame>
    )
  }

const TextInputCell = forwardRef(
  ({ titleProps, ...props }: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleOfficialInformationEdit')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          titleProps={{ ...titleProps, variant: 'h5', color: 'text.primary' }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

const TextInputLinkCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleOfficialInformationEdit')
    return (
      <CellFrame>
        <Typography variant="h5">{t('linkTitle')}</Typography>
        <Typography variant="body2" color="text.secondary" pt={1}>
          {t('linkNote')}
        </Typography>
        <TextInputField
          {...props}
          titleProps={{ sx: { display: 'none' } }}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  submitting: boolean
  disabled: boolean
  onSubmit: () => void
}
const ButtonCell: Cell<ButtonCellProps> = ({
  submitting,
  disabled,
  onSubmit,
}) => {
  const { t } = useTranslation('consoleOfficialInformationEdit')

  return (
    <Stack alignItems="center" pb={6}>
      <Button
        size="large"
        sx={{ width: 212 }}
        loading={submitting}
        disabled={disabled}
        onClick={onSubmit}
      >
        {t('save')}
      </Button>
    </Stack>
  )
}

const defaultValues: ConsoleOfficialInformationEditFormValues = {
  title: '',
  body: '',
  link: '',
}
